import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { Input, Popover, VStack } from 'govwell-ui'
import { useMenuKeyboardShortcuts } from 'govwell-ui/components/Menu/use-menu-keyboard-shortcuts'

import {
  OrganizationMembership,
  useMyOrganizationMemberships,
} from '../../hooks/use-my-organization-memberships'
import { useMyOrgId } from '../../layouts/AppStateContextLayout/utils'
import { switchActiveOrg } from '../../utils/org-storage'

import OrgRow, { getOrgLabel } from './OrgRow'

const SEARCH_THRESHOLD = 5

type Props = {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  children: React.ReactNode
}

const OrgSwitcherPopover = (props: Props) => {
  const { isOpen, onOpen, onClose, children } = props
  const memberships = useMyOrganizationMemberships()
  const currentOrgId = useMyOrgId()
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [searchQuery, setSearchQuery] = useState('')

  const showSearch = memberships.length > SEARCH_THRESHOLD

  const filteredMemberships = useMemo(() => {
    if (!searchQuery) {
      return memberships
    }
    const query = searchQuery.toLowerCase()
    return memberships.filter((m) => getOrgLabel(m).toLowerCase().includes(query))
  }, [memberships, searchQuery])

  const currentOrgIndex = useMemo(
    () =>
      Math.max(
        0,
        filteredMemberships.findIndex((m) => m.organizationId === currentOrgId)
      ),
    [filteredMemberships, currentOrgId]
  )

  const [activeIndex, setActiveIndex] = useState(currentOrgIndex)

  const prevIsOpenRef = useRef(false)
  // Reset active index and search query when the popover first opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setActiveIndex(currentOrgIndex)
      setSearchQuery('')
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, currentOrgIndex])

  // Reset active index when filtered results change (e.g. during search)
  useEffect(() => {
    setActiveIndex(0)
  }, [filteredMemberships.length])

  // Focus search input when popover opens
  const handleOpenAutoFocus = useCallback(
    (e: Event) => {
      if (showSearch) {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    },
    [showSearch]
  )

  const handleSelect = useCallback(
    (membership: OrganizationMembership) => {
      if (membership.organizationId === currentOrgId) {
        onClose()
        return
      }
      switchActiveOrg(membership.organizationId)
    },
    [currentOrgId, onClose]
  )

  const handleKeyboardSelect = useCallback(() => {
    const membership = filteredMemberships[activeIndex]
    if (membership) {
      handleSelect(membership)
    }
  }, [filteredMemberships, activeIndex, handleSelect])

  const { menuItemRefs } = useMenuKeyboardShortcuts({
    activeIndex,
    count: filteredMemberships.length,
    isEnabled: isOpen && filteredMemberships.length > 0,
    onEscape: onClose,
    onSelect: handleKeyboardSelect,
    setActiveIndex,
  })

  const rowRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      menuItemRefs.current.set(index, el)
    },
    [menuItemRefs]
  )

  const orgRows = useMemo(
    () =>
      filteredMemberships.map((membership, index) => (
        <OrgRow
          key={membership.id}
          membership={membership}
          index={index}
          isCurrentOrg={membership.organizationId === currentOrgId}
          isHighlighted={index === activeIndex}
          setActiveIndex={setActiveIndex}
          onSelect={handleSelect}
          rowRef={rowRef(index)}
        />
      )),
    [filteredMemberships, currentOrgId, activeIndex, rowRef, handleSelect]
  )

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Content
        side="top"
        align="start"
        sideOffset={8}
        className="w-80 p-1"
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        {showSearch && (
          <Input
            ref={searchInputRef}
            ariaLabel="Search organizations"
            className="mx-1 mt-1 mb-1.5"
            placeholder="Search organizations..."
            prefixIcon={faSearch}
            value={searchQuery}
            onValueChange={setSearchQuery}
            isClearable
          />
        )}
        <VStack gap={0} align="stretch" maxHeight="288px" className="overflow-y-auto">
          {orgRows}
          {filteredMemberships.length === 0 && (
            <p className="px-3 py-2 text-center text-sm text-neutral-500">No results</p>
          )}
        </VStack>
      </Popover.Content>
    </Popover>
  )
}

export default React.memo(OrgSwitcherPopover)
